import React, { useMemo } from "react";
import type { CalendarDate } from "../lib/dateUtils";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

interface MiniMonthProps {
  activeDate: CalendarDate;
  onSelectDate: (date: CalendarDate) => void;
  onNavigateMonth: (delta: number) => void;
}

interface MiniDayCell {
  date: CalendarDate;
  currentMonth: boolean;
  isToday: boolean;
  isActive: boolean;
}

function buildMonthGrid({ year, month, day }: CalendarDate): MiniDayCell[] {
  const firstOfMonth = new Date(year, month - 1, 1);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - start.getDay());
  const cells: MiniDayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const cellDate = new Date(start);
    cellDate.setDate(start.getDate() + i);
    const cell: MiniDayCell = {
      date: {
        year: cellDate.getFullYear(),
        month: cellDate.getMonth() + 1,
        day: cellDate.getDate(),
      },
      currentMonth: cellDate.getMonth() === firstOfMonth.getMonth(),
      isToday: isSameDay(cellDate, new Date()),
      isActive:
        cellDate.getFullYear() === year &&
        cellDate.getMonth() + 1 === month &&
        cellDate.getDate() === day,
    };
    cells.push(cell);
  }
  return cells;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function MiniMonth({ activeDate, onSelectDate, onNavigateMonth }: MiniMonthProps) {
  const grid = useMemo(() => buildMonthGrid(activeDate), [activeDate]);

  const monthLabel = useMemo(() => {
    const d = new Date(activeDate.year, activeDate.month - 1, 1);
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(d);
  }, [activeDate]);

  return (
    <div className="mini-month">
      <header>
        <button
          type="button"
          className="btn-icon"
          onClick={() => onNavigateMonth(-1)}
          aria-label="Previous month"
        >
          «
        </button>
        <span>{monthLabel}</span>
        <button
          type="button"
          className="btn-icon"
          onClick={() => onNavigateMonth(1)}
          aria-label="Next month"
        >
          »
        </button>
      </header>
      <div className="mini-month-grid">
        {DAYS.map((d, i) => (
          <span key={i} className="mini-month-day-label">
            {d}
          </span>
        ))}
        {grid.map((cell) => (
          <button
            key={`${cell.date.year}-${cell.date.month}-${cell.date.day}`}
            type="button"
            className={[
              "mini-month-cell",
              cell.currentMonth ? "" : "muted",
              cell.isActive ? "active" : "",
              cell.isToday ? "today" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onSelectDate(cell.date)}
          >
            {cell.date.day}
          </button>
        ))}
      </div>
    </div>
  );
}
